import Cookies from 'js-cookie';

import propertyUtils from './propertyUtils';

import SplitTestConstants from '../constants/SplitTestConstants';
import PropertyConstants from '../constants/PropertyConstants';

import type { SplitExperimentsProps } from '@zg-rentals/split';
import type { RentGuaranteeProps } from '../model/RentGuarantee';
import type { CurrentPropertyProps } from '../model/CurrentProperty';
import type { UserProps } from '../model/User';
import type { AppConfigProps } from '../model/AppConfig';
import { CometUserRoleType } from '@zg-rentals/ts-rental-manager-api-client';
export type RestrictionsProps = {
  applications?: Record<string, unknown>;
  [key: string]: boolean | undefined | Record<string, unknown>;
};

type CookieEnabledServicesProps = {
  [key: string]: string | boolean;
};

type PossibleFeatureName = (typeof SplitTestConstants)[keyof typeof SplitTestConstants];
const getSplitExperimentTreatment = (
  splitExperiments: SplitExperimentsProps = {},
  featureName: PossibleFeatureName | '' = '',
) => splitExperiments?.[featureName]?.treatment === 'on';

const canAccessApplications = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) => {
  const hasApplicationsProperty = restrictions.hasOwnProperty('applications');
  // isAccessRestricted controls whether the application tab (in the subnav) should be  hidden or displayed
  return !hasApplicationsProperty || !restrictions?.applications?.isAccessRestricted;
};

const canUpdateApplications = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) => {
  const hasApplicationsProperty = restrictions.hasOwnProperty('applications');
  // isUpdateRestricted indicates whether the user is allowed to enable or disable applications on a listing
  return !hasApplicationsProperty || !restrictions?.applications?.isUpdateRestricted;
};

const canAccessLeaseBuilder = ({ restrictions = {} }: { restrictions?: RestrictionsProps }) =>
  !restrictions.isLeaseBuilderRestricted;

const canFeedConnectSwitchSource = ({ user }: { user: UserProps }) => user?.features?.canSwitchListingSource;

const canAccessListingClaiming = ({ user }: { user: UserProps }) => user?.features?.canAccessFeedClaimAndConvert;

const canAccessModularListing = ({
  cookieEnabledServices,
  currentProperty,
  user,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  currentProperty: CurrentPropertyProps;
  user: UserProps;
}) => {
  const { dateLastActivatedTimestamp = '' } = currentProperty;
  const {
    features: { canAccessModularListing },
  } = user;

  const hasListingBeenActivated = dateLastActivatedTimestamp !== '';
  const isSingleFamilyRental = propertyUtils.isSingleFamilyRental({ currentProperty });
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const response =
    !hasListingBeenActivated &&
    (isSingleFamilyRental || isRoomForRent) &&
    (canAccessModularListing || !!cookieEnabledServices.ZRM_MODULAR_LISTING_ENABLED);

  return response;
};

const canAccessAddFeedListingsModal = ({ user }: { user: UserProps }) => user?.features?.canViewClaimFeedListing;

const canAccessRentalProtectionFromML = ({
  appConfig,
  rentalProtection,
}: {
  appConfig: AppConfigProps;
  rentalProtection: RentGuaranteeProps;
}) => {
  const { isEligible: isRentGuaranteeEligible } = rentalProtection;
  const {
    config: {
      features: { canAccessRentalProtectionFromML },
    },
  } = appConfig;

  const areRequiredConditionsSatisfied = isRentGuaranteeEligible && canAccessRentalProtectionFromML;

  return areRequiredConditionsSatisfied;
};

const canAccessRentalProtectionLightweightOptinExperiment = ({ appConfig }: { appConfig: AppConfigProps }) => {
  return appConfig?.config?.features?.canAccessRentalProtectionLightweightOptin;
};

const canAccessRentalProtectionFuturePolicyCancellation = (appConfig: AppConfigProps) => {
  return appConfig?.config?.features?.canAccessRentalProtectionFuturePolicyCancellation;
};

const canAccessRentGuaranteeWithFeatureFlag = ({
  cookieEnabledServices,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
}) => Boolean(cookieEnabledServices.ZRM_RENT_GUARANTEE_ENABLED);

const canAccessPostingPathTourScheduling = ({
  appConfig,
  cookieEnabledServices,
  splitExperiments,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
  splitExperiments: SplitExperimentsProps;
}) => {
  const isInExperimentTreatment = getSplitExperimentTreatment(
    splitExperiments,
    SplitTestConstants.ZRM_TOUR_SCHEDULING_INTEGRATION,
  );

  return (
    (appConfig?.config?.features?.canAccessPostingPathTourScheduling ||
      cookieEnabledServices?.ZRM_POSTING_PATH_TOUR_SCHEDULING_ENABLED) &&
    isInExperimentTreatment
  );
};

const isTourSchedulingEnabledForProperty = ({
  appConfig,
  cookieEnabledServices,
  currentProperty,
  isInReactivationFlow,
  splitExperiments,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
  currentProperty: CurrentPropertyProps;
  isInReactivationFlow: boolean;
  splitExperiments: SplitExperimentsProps;
}) => {
  const { active, canBeRelisted, isFeed } = currentProperty;
  const canAccessTourScheduling = canAccessPostingPathTourScheduling({
    appConfig,
    cookieEnabledServices,
    splitExperiments,
  });
  const canAccessRelisting = canAccessRelistingFlow({ canBeRelisted });
  return Boolean(canAccessTourScheduling && !isFeed && !(isInReactivationFlow && !canAccessRelisting && !active));
};

const canAccessRentersInsuranceFormField = ({
  cookieEnabledServices,
  isEligibleForIsRentersInsuranceRequiredQuestion,
  splitExperiments = {},
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  isEligibleForIsRentersInsuranceRequiredQuestion: boolean;
  splitExperiments?: SplitExperimentsProps;
}) => {
  // currently there is 100% allocation to the experiment treatment on QA
  const isInExperimentTreatment = getSplitExperimentTreatment(
    splitExperiments,
    SplitTestConstants.ZRM_RENTERS_INSURANCE_LISTING_PATH,
  );

  const areRequiredConditionsSatisfied = isEligibleForIsRentersInsuranceRequiredQuestion && isInExperimentTreatment;
  const areOverrideConditionsSatisfied = cookieEnabledServices?.ZRM_RENTERS_INSURANCE_FORM_FIELD_ENABLED;

  return areRequiredConditionsSatisfied || !!areOverrideConditionsSatisfied;
};

const canAccessDashboardRedesign = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  return currentProperty?.isMultifamily === false;
};

const canAccessGlobalAnalytics = ({ user }: { user: UserProps }) => user.features.canAccessMainAnalytics;

const canAccessGlobalContacts = ({ user }: { user: UserProps }) => user.features.canAccessMainContacts;

const canAccessBilling = ({ user }: { user: UserProps }) => user.features.canAccessBilling;

const canAccessSettings = ({ user }: { user: UserProps }) => user.features.canAccessMainSecuritySettings;

const canAccessUserManagement = ({ user }: { user: UserProps }) => user.features.canAccessUserManagement;

const canAccessUserManagementApplicationPermission = ({ user }: { user: UserProps }) =>
  user.features.canAccessUserManagementApplicationPermission;

const canAddListing = ({ user }: { user: UserProps }) => user.features.canAddListing;

const canAccessInvoicePayments = ({ user }: { user: UserProps }) => user.features.canAccessInvoicePayments;

const canEditMFFeedContact = ({
  cookieEnabledServices,
  formData,
  appConfig,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  formData: CurrentPropertyProps;
  appConfig: AppConfigProps;
}) => {
  const {
    config: {
      features: { canEditMFFeedContact },
    },
  } = appConfig;
  const {
    restrictions: { isUpdateListingRestricted },
    isFeed,
    isContactInfoEditable,
  } = formData || {};

  return (
    isFeed &&
    isUpdateListingRestricted === false &&
    isContactInfoEditable &&
    (canEditMFFeedContact || cookieEnabledServices.ZRM_EDIT_MF_FEED_CONTACT_ENABLED)
  );
};

const canAccessFeatureNotifications = ({
  cookieEnabledServices,
  user,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  user: UserProps;
}) =>
  user.features.canAccessFeatureLaunchNotifications || Boolean(cookieEnabledServices.ZRM_FEATURE_NOTIFICATION_ENABLED);

const canAccessOnboardingWizard = ({
  cookieEnabledServices,
  user,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  user: UserProps;
}) => {
  return (
    user?.restrictions?.isAllowed &&
    (Boolean(cookieEnabledServices.ZRM_ONBOARDING_WIZARD_ENABLED) || user.features.canViewOnboardingFlow)
  );
};

const canAccessTopOfFunnelPremium = ({
  isFreemiumEnabled,
  isTopOfFunnelEnrollmentEligible,
  isActiveProductTypePremium,
}: {
  isFreemiumEnabled: boolean;
  isTopOfFunnelEnrollmentEligible: boolean;
  isActiveProductTypePremium: boolean;
}) => {
  return isFreemiumEnabled && (isActiveProductTypePremium || isTopOfFunnelEnrollmentEligible);
};

const canAccessMLReactivationFlow = ({ currentProperty }: { currentProperty: Partial<CurrentPropertyProps> }) => {
  return (currentProperty?.dateLastActivatedTimestamp || currentProperty?.isFeed) && !currentProperty?.isMultifamily;
};

const canAccessFastPost = ({
  appConfig,
  currentProperty,
}: {
  appConfig: AppConfigProps;
  currentProperty: CurrentPropertyProps;
}) => {
  return (
    !currentProperty?.isMultifamily &&
    !currentProperty?.dateLastActivatedTimestamp &&
    appConfig?.config?.features?.canAccessFastPost &&
    !currentProperty?.isFeed &&
    !currentProperty?.restrictions?.applications?.isAccessRestricted
  );
};

const canAccessCloneListing = ({ currentProperty }: { currentProperty: CurrentPropertyProps }) => {
  return currentProperty?.restrictions?.isCloneListingRestricted;
};

const canAccessMaintenanceTracker = ({
  canAccessMaintenanceTracker,
  cookieZGMaintenanceTracker,
}: {
  canAccessMaintenanceTracker: UserProps;
  cookieZGMaintenanceTracker: CookieEnabledServicesProps;
}) => {
  return cookieZGMaintenanceTracker || canAccessMaintenanceTracker;
};

const canAccessManagePolicyPageV2 = (appConfig: AppConfigProps) =>
  appConfig?.config?.features?.canAccessManagePolicyPageV2;

const canAccessRelistingFlow = ({ canBeRelisted }: { canBeRelisted: boolean }) => {
  return canBeRelisted;
};

const canAccessListingPreview = ({
  appConfig,
  currentProperty,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
  currentProperty: CurrentPropertyProps;
}) => {
  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  return (
    !currentProperty?.active &&
    appConfig?.config?.features?.canAccessListingPreview &&
    !isRoomForRent &&
    !currentProperty?.isFeed &&
    !currentProperty?.isMultifamily
  );
};

const canAccessNoWelcomePageML = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessNoWelcomePageML;

const canAccessPostActivationWorkflow = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessPostActivationWorkflow;

const canAccessScreeningCriteria = ({
  isUserEligibleForListingCriteria,
  isListingCriteriaEnabled,
}: {
  isUserEligibleForListingCriteria: boolean;
  isListingCriteriaEnabled: boolean;
}) => {
  return Boolean(isUserEligibleForListingCriteria && isListingCriteriaEnabled);
};

const canAccessMultiBoxVerificationInput = ({
  cookieEnabledServices,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
}) => cookieEnabledServices?.ZRM_MULTI_BOX_VERIFICATION_INPUT_ENABLED;

const canAccessMultiplex = ({
  canAccessMultiplexResponse,
  cookieEnabledServices,
}: {
  canAccessMultiplexResponse: string;
  cookieEnabledServices: CookieEnabledServicesProps;
}) => {
  const areRequiredConditionsSatisfied = Boolean(canAccessMultiplexResponse === 'true');
  const areOverrideConditionsSatisfied = cookieEnabledServices?.ZRM_MULTIPLEX_ENABLED;

  return areRequiredConditionsSatisfied || areOverrideConditionsSatisfied;
};

const canAccessRequestWidget = ({
  appConfig,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
}) => appConfig?.config?.features?.canAccessSubmitRequestWidget;

const canAccessNewRequestForm = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessNewSupportForm;

const canAccessLandingPageMonetization = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessLandingPageMonetization;

const canAccessZRMMarketingComponents = ({
  appConfig,
  cookieEnabledServices,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
}) =>
  appConfig?.config?.features?.canAccessZRMMarketingComponents ||
  cookieEnabledServices?.ZRM_MARKETING_COMPONENTS_ENABLED;

const canAccessSFCostsAndFees = ({ isFeed, isMultifamily }: { isFeed: boolean; isMultifamily: boolean }) =>
  !isFeed && !isMultifamily;

const canAccessTenantPlacement = ({
  enabledStates,
  state,
  propertyType,
  currentProperty,
  user,
  cookie,
}: {
  enabledStates: string;
  state?: string;
  propertyType?: string;
  currentProperty?: CurrentPropertyProps;
  user: UserProps;
  cookie?: string;
}) => {
  const propertyState = state ? state : currentProperty?.state;

  const isInPilotState = propertyState ? enabledStates.split(',').includes(propertyState) : false;

  const isMultifamily = propertyType
    ? propertyType === PropertyConstants.PROPERTY_TYPES.COMMUNITY
    : currentProperty?.isMultifamily;

  return (
    (!cookie || !(Cookies.get(cookie) === 'true')) && !user.isEnterpriseCustomer && !isMultifamily && isInPilotState
  );
};

const canAccessTenantPlacementListingModuleBanner = ({
  currentProperty,
  user,
  enabledStates,
}: {
  currentProperty: CurrentPropertyProps;
  user: UserProps;
  enabledStates: string;
}) => {
  const hasListingBeenActivated = Boolean(currentProperty.dateLastActivatedTimestamp);
  return canAccessTenantPlacement({ enabledStates, currentProperty, user }) && hasListingBeenActivated;
};

const isNomadPartnerEnabled = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.isNomadPartnerEnabled;

const canAccessB2BLayout = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessB2BLayout;

const isNonAdvertiser = ({ user }: { user: UserProps }) =>
  user?.roles?.includes(CometUserRoleType.NON_ADVERTISER_CUSTOMER);

const canAccessClaimPath = ({
  cookie,
  hasAnyPropertiesToClaim,
  isScreenMobile,
  isNonAdvertiser,
}: {
  cookie?: string;
  hasAnyPropertiesToClaim: boolean;
  isScreenMobile: boolean;
  isNonAdvertiser: boolean;
}) => !isScreenMobile && (hasAnyPropertiesToClaim || isNonAdvertiser) && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessClaimMorePath = ({
  isScreenMobile,
  hasAnyClaimedListings,
  hasNewPropertiesToClaim,
  cookie,
}: {
  isScreenMobile: boolean;
  hasAnyClaimedListings: boolean;
  hasNewPropertiesToClaim: boolean;
  cookie: string;
}) =>
  !isScreenMobile && hasAnyClaimedListings && hasNewPropertiesToClaim && (!cookie || Cookies.get(cookie) !== 'true');

const canAccessTenantsModule = ({ canAccessTenantModuleResponse }: { canAccessTenantModuleResponse: string }) => {
  return Boolean(canAccessTenantModuleResponse === 'true');
};

const shouldShowBrokerFeeAlert = ({
  state,
  brokerFeeAlertStatesResponse,
}: {
  state: string;
  brokerFeeAlertStatesResponse: string;
}) => {
  return state ? brokerFeeAlertStatesResponse.split(',').includes(state) : false;
};

const canPostForSaleListing = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canPostForSaleListing;

const isLegacyTourDisabled = ({
  appConfig,
  cookieEnabledServices,
}: {
  appConfig: AppConfigProps;
  cookieEnabledServices: CookieEnabledServicesProps;
}) => appConfig?.config?.features?.isLegacyTourDisabled || cookieEnabledServices?.ZRM_LEGACY_TOUR_DISABLED;

const canAccessValueOfPremium = ({ splitExperiments }: { splitExperiments?: SplitExperimentsProps }) => {
  return getSplitExperimentTreatment(splitExperiments, SplitTestConstants.ZRM_VALUE_OF_PREMIUM);
};

const canAccessListingQuality = ({
  user,
  currentProperty,
}: {
  user: UserProps;
  currentProperty?: CurrentPropertyProps;
}) =>
  Boolean(user.isMultifamilyCustomer || isNonAdvertiser({ user })) &&
  (!currentProperty || Boolean(currentProperty?.isMultifamily));

const canAccessProofOfInsurance = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessProofOfInsuranceModule;

const canAccessPropertyDescriptionReorder = ({
  cookieEnabledServices,
  splitExperiments,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  splitExperiments: SplitExperimentsProps;
}) =>
  Boolean(cookieEnabledServices?.ZRM_DESCRIPTION_REORDER_ENABLED) ||
  getSplitExperimentTreatment(splitExperiments, SplitTestConstants.ZRM_PROPERTY_DESCRIPTION_REORDER);

const canAccessTotalMonthlyPriceWithFee = ({
  cookieEnabledServices,
  canAccessTotalMonthlyPriceWithFee = 'false',
  isEligible = false,
}: {
  cookieEnabledServices: CookieEnabledServicesProps;
  canAccessTotalMonthlyPriceWithFee?: string;
  isEligible?: boolean;
}) =>
  Boolean(cookieEnabledServices?.ZRM_TOTAL_MONTHLY_PRICE_ENABLED) ||
  (canAccessTotalMonthlyPriceWithFee === 'true' && isEligible);

const canAccessSteppedFee = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canAccessSteppedFee;

const canEditTourDuration = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canEditTourDuration;

const canRescheduleTours = ({ appConfig }: { appConfig: AppConfigProps }) =>
  appConfig?.config?.features?.canRescheduleTours;

export default {
  canAccessAddFeedListingsModal,
  canAccessApplications,
  canAccessBilling,
  canAccessClaimPath,
  canAccessClaimMorePath,
  canAccessCloneListing,
  canAccessDashboardRedesign,
  canAccessFastPost,
  canAccessFeatureNotifications,
  canAccessGlobalAnalytics,
  canAccessGlobalContacts,
  canAccessInvoicePayments,
  canAccessLandingPageMonetization,
  canAccessLeaseBuilder,
  canAccessListingClaiming,
  canAccessListingPreview,
  canAccessMLReactivationFlow,
  canAccessMaintenanceTracker,
  canAccessManagePolicyPageV2,
  canAccessModularListing,
  canAccessMultiBoxVerificationInput,
  canAccessMultiplex,
  canAccessNewRequestForm,
  canAccessNoWelcomePageML,
  canAccessOnboardingWizard,
  canAccessPostActivationWorkflow,
  canAccessRelistingFlow,
  canAccessRentGuaranteeWithFeatureFlag,
  canAccessRentalProtectionFromML,
  canAccessRentalProtectionFuturePolicyCancellation,
  canAccessRentalProtectionLightweightOptinExperiment,
  canAccessRentersInsuranceFormField,
  canAccessRequestWidget,
  canAccessScreeningCriteria,
  canAccessSettings,
  canAccessTopOfFunnelPremium,
  canAccessUserManagement,
  canAccessUserManagementApplicationPermission,
  canAccessZRMMarketingComponents,
  canAddListing,
  canEditMFFeedContact,
  canFeedConnectSwitchSource,
  canUpdateApplications,
  canAccessTenantPlacement,
  canAccessTenantPlacementListingModuleBanner,
  canAccessB2BLayout,
  canAccessSFCostsAndFees,
  isNomadPartnerEnabled,
  canAccessTenantsModule,
  canPostForSaleListing,
  canAccessPostingPathTourScheduling,
  isTourSchedulingEnabledForProperty,
  isLegacyTourDisabled,
  canAccessValueOfPremium,
  canAccessListingQuality,
  canAccessProofOfInsurance,
  isNonAdvertiser,
  canAccessPropertyDescriptionReorder,
  canAccessTotalMonthlyPriceWithFee,
  canAccessSteppedFee,
  shouldShowBrokerFeeAlert,
  canEditTourDuration,
  canRescheduleTours,
};
